/**
 * The briefs' email on the Briefs page (Foundry Intelligence, build order step 5; GET and PUT /v1/twin/briefs/email):
 * which roles each periodic cadence's new briefs are emailed to, as an org admin ticks them, and the page's words about
 * it. Every brief is on the page whatever is chosen here. The email is extra, and the platform sends none while its own
 * switch is off (`platform_enabled`), whatever the org chose: saving then only chooses who will get it once it is on.
 * The overall brief is never emailed (every analysis run rewrites its one row), so it has no row here. Pure; no em or
 * en dashes (house style).
 */
import type { BriefEmailCadence, BriefEmailCadences, BriefEmailRole } from "@/lib/api/brief";

/** The periodic cadences, in the order the Briefs page's tabs show them. */
export const EMAIL_CADENCES: readonly { id: BriefEmailCadence; label: string }[] = [
  { id: "shift", label: "Shift" },
  { id: "day", label: "Day" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
  { id: "year", label: "Year" },
];

/** The org's roles, in the order of what they may do; the server keeps a cadence's roles in this order too. */
export const EMAIL_ROLES: readonly { id: BriefEmailRole; label: string; plural: string }[] = [
  { id: "viewer", label: "Viewer", plural: "viewers" },
  { id: "operator", label: "Operator", plural: "operators" },
  { id: "technician", label: "Technician", plural: "technicians" },
  { id: "member", label: "Member", plural: "members" },
  { id: "admin", label: "Admin", plural: "admins" },
  { id: "owner", label: "Owner", plural: "owners" },
];

/** No role ticked for any cadence: nobody is emailed. */
export function noEmail(): BriefEmailCadences {
  return { shift: [], day: [], week: [], month: [], year: [] };
}

/** The choice with one role ticked (on) or not for one cadence. The roles stay in their own order, each once. */
export function withEmailRole(
  choice: BriefEmailCadences,
  cadence: BriefEmailCadence,
  role: BriefEmailRole,
  on: boolean,
): BriefEmailCadences {
  const ticked = new Set(choice[cadence]);
  if (on) ticked.add(role);
  else ticked.delete(role);
  return { ...choice, [cadence]: EMAIL_ROLES.map((r) => r.id).filter((r) => ticked.has(r)) };
}

/** Whether two choices tick the same roles for every cadence, in any order (Save is offered only for a change). */
export function sameEmailChoice(a: BriefEmailCadences, b: BriefEmailCadences): boolean {
  return EMAIL_CADENCES.every(({ id }) => {
    const x = [...a[id]].sort();
    const y = [...b[id]].sort();
    return x.length === y.length && x.every((r, i) => r === y[i]);
  });
}

export const EMAIL_TITLE = "Email the briefs";

/** What the email is, which briefs it carries, and who may change it. */
export function emailLead(canEdit: boolean): string {
  const lead =
    "Every brief is on this page. An org admin can also have each new shift, day, week, month or year brief emailed to the members who hold the roles ticked for its cadence. The overall brief is not emailed: it stays on this page.";
  return canEdit ? lead : `${lead} Only an org admin can change who gets them.`;
}

/** Whether the platform sends the email at all, and what saving does while it does not. */
export function emailPlatformNote(s: { platformEnabled: boolean; canEdit: boolean }): string {
  if (s.platformEnabled) {
    return "Email is on for this platform: each new brief goes once to every member who holds a ticked role, with the consultant's summary or the brief's own figures and a link back here.";
  }
  const off = "Email is off on this platform, so no brief is emailed yet.";
  return s.canEdit
    ? `${off} Saving only chooses who will get each brief once it is on.`
    : `${off} The roles ticked here will get each brief once it is on.`;
}

/** The owner role reaches the org's owner too, whatever role the owner's membership shows. */
export const EMAIL_OWNER_NOTE = "Owner also reaches the org's owner.";

/** A box's name for a screen reader: "Email the day brief to admins". */
export function emailBoxLabel(cadence: BriefEmailCadence, role: BriefEmailRole): string {
  const c = EMAIL_CADENCES.find((x) => x.id === cadence)?.label.toLowerCase() ?? cadence;
  const r = EMAIL_ROLES.find((x) => x.id === role)?.plural ?? role;
  return `Email the ${c} brief to ${r}`;
}

export const EMAIL_SAVED = "Saved.";

/** A failed save, in the page's words: the server's own detail where it gave one. */
export function emailSaveError(detail?: string): string {
  return detail ? `The choice was not saved: ${detail}` : "The choice was not saved. Try again.";
}
